/**
 * #10 商品列表頁 — LIFF /products
 *
 * 商品按 category 分組(即飲線 / 鮮燉線),每張卡有 hero image + 加入購物車按鈕。
 * 右上角小購物車圖標顯示目前數量,點下去去 /cart。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { getProducts, type Product, type ProductCategory, type ProductDetail } from "../api/cart";
import { useCartStore } from "../store/cartStore";
import { useUIStore } from "../store/uiStore";


export default function Products() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const cart = useCartStore((s) => s.cart);
  const refresh = useCartStore((s) => s.refresh);

  useEffect(() => {
    (async () => {
      try {
        const cats = await getProducts();
        setCategories(cats);
        await refresh();
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  // URL hash 跳轉 — 從品味誌 Related Product 卡點過來會帶 #product-{id},自動 scroll + highlight
  useEffect(() => {
    if (loading || categories.length === 0) return;
    const hash = window.location.hash;
    if (!hash || !hash.startsWith("#product-")) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // 短暫高亮 2 秒讓 user 知道對應商品在這
      el.classList.add("ring-2", "ring-coral", "ring-offset-2");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-coral", "ring-offset-2");
      }, 2000);
    }, 200);
    return () => clearTimeout(t);
  }, [loading, categories]);

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted">載入中…</div>;
  }

  const cartCount = cart?.items_count || 0;

  return (
    <div className="min-h-screen bg-cream pb-24">
      <header className="bg-primary text-white px-5 py-6 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/home")} className="text-white text-xl">‹</button>
          <h1 className="text-xl font-bold">恆時滋養計畫</h1>
        </div>
        <button
          onClick={() => navigate("/cart")}
          aria-label={`購物車${cartCount > 0 ? `(${cartCount} 件)` : "(空)"}`}
          className="relative text-white text-2xl"
        >
          <ShoppingBag size={24} strokeWidth={1.5} aria-hidden="true" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-2 bg-accent text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </button>
      </header>

      {categories.map((cat) => (
        <section key={cat.key} className="px-5 mt-5">
          <div className="text-lg font-bold text-ink">{cat.title}</div>
          <div className="text-xs text-muted">{cat.subtitle}</div>
          <div className="space-y-4 mt-3">
            {cat.items.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
          {cat.detail && <ProductDetailAccordion detail={cat.detail} />}
        </section>
      ))}
    </div>
  );
}


function ProductCard({ product }: { product: Product }) {
  const add = useCartStore((s) => s.add);
  const [busy, setBusy] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  async function handleAdd() {
    setBusy(true);
    try {
      await add(product.id, 1);
      showToast(`已加入「${product.name}」`, "success");
    } catch {
      showToast("加入失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id={`product-${product.id}`} className="card overflow-hidden p-0">
      {product.image_url && (
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full aspect-[4/3] object-cover"
          loading="lazy"
        />
      )}
      <div className="p-4 space-y-2">
        <div className="text-base font-bold text-ink">{product.name}</div>
        {product.tagline && <div className="text-xs text-muted">{product.tagline}</div>}
        <div className="flex items-center justify-between mt-2">
          <div>
            <div className="text-xs text-muted">{product.spec}</div>
            <div className="text-lg font-bold text-ink mt-0.5">NT$ {product.price.toLocaleString()}</div>
          </div>
          <button
            onClick={handleAdd}
            disabled={busy}
            className="btn-primary text-sm px-4 py-2"
          >
            {busy ? "加入中…" : "加入購物車"}
          </button>
        </div>
        {/* 訂閱方案 Phase 2 開放 — 預留 stub 按鈕讓 user 知道方向 */}
        <button
          disabled
          className="w-full text-center text-xs text-muted py-2 border border-dashed border-line rounded-lg mt-2 cursor-not-allowed"
          aria-label="定期訂閱方案 即將推出"
        >
          定期訂閱方案 · 即將推出
        </button>
      </div>
    </div>
  );
}


// 商品詳情 — 品牌共用一份(目前只有鮮燉),分類層收合卡,預設收起
function ProductDetailAccordion({ detail }: { detail: ProductDetail }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card p-0 mt-4 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-bold text-ink">商品詳情 · 品質 · 規格 · 保存 · 配送</span>
        <span className={`text-muted transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-3 space-y-3 border-t border-line">
          {detail.intro && <p className="text-xs text-muted leading-relaxed">{detail.intro}</p>}
          {detail.sections.map((sec, i) => (
            <div key={i} className="space-y-1.5">
              <div className="text-xs font-bold text-accent">{sec.title}</div>
              {sec.rows.map((row, j) =>
                row.label ? (
                  <div key={j} className="flex gap-2 text-xs">
                    <span className="shrink-0 min-w-[72px] text-muted">{row.label}</span>
                    <span className="text-ink">{row.value}</span>
                  </div>
                ) : (
                  <p key={j} className={`text-xs leading-relaxed ${row.muted ? "text-muted" : "text-ink"}`}>
                    {row.text}
                  </p>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
